import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { deviceToLinkElement } from "../../navigation/deviceNavigationUtils";
import { useDeviceCardStyles } from "../DeviceCard.styles";
import { IDeviceCardProps } from "../DeviceCard";
import { AlarmsAndOperationModeRendererForTEM } from "../../dataPointRenderers/alarmsAndOperationModeRenderer";
import engine from "../../../images/AVAT_Piktogramm_Gasmotor_RGB.png";
import LabeledIconRenderer from "../../dataPointRenderers/labeledIconRenderer";
import { useTEMDeviceCardValues, TEMDeviceCardValues } from "./useTEMDeviceCardValues";
import { useStyles, useCardHeaderStyles, useCardBodyAndActionStyles } from "./TEMDeviceCard.styles";

import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import { Grid, CardHeader, Avatar, Typography } from "@material-ui/core";
import OperationhoursIcon from "@material-ui/icons/UpdateOutlined";
import EngineStartsIcon from "@material-ui/icons/PowerSettingsNewOutlined";
import NextMaintenanceIcon from "@material-ui/icons/BuildOutlined";
import PowerIcon from "@material-ui/icons/SpeedRounded";
import Hidden from "@material-ui/core/Hidden";
import LanguageContext from "../../hoc/LanguageContextProvider";
import { Trans } from "@lingui/macro";
import OperationStateIcon from "@material-ui/icons/InfoOutlined";

import {
  getAnalogValueLabel,
  getDiscreteValueLabel,
  getDeviceEngineName,
  getDevicePlantName,
  getFormattedDateTimeLabel,
} from "../deviceCardUtils";
import { useLingui } from "@lingui/react";

const TEMDeviceCard: React.FunctionComponent<IDeviceCardProps> = ({ device }) => {
  const commonCardStyles = useDeviceCardStyles();
  const classes = useStyles();

  const cardHeaderStyles = useCardHeaderStyles();
  const cardBodyAndActionStyles = useCardBodyAndActionStyles();
  const { i18n } = useLingui();

  const { currentLanguage } = useContext(LanguageContext);
  const { loading, error, deviceCardValues } = useTEMDeviceCardValues(device);

  const isLiveContentAvailable = !loading && !error && device.dataModel && deviceCardValues;

  return (
    <Card className={`${commonCardStyles.root} TEMDeviceCard`}>
      <CardHeader
        avatar={<Avatar aria-label="TEM Engine" className={classes.avatar} src={engine} />}
        action={
          isLiveContentAvailable && (
            <AlarmsAndOperationModeRendererForTEM
              device={device}
              failuresAvailable={deviceCardValues?.FailuresAvailable}
              alarmsAvailable={deviceCardValues?.AlarmsAvailable}
              operationMode={deviceCardValues?.OperationMode}
            />
          )
        }
        title={getDeviceEngineName(device)}
        subheader={getDevicePlantName(device)}
        classes={cardHeaderStyles}
      />

      <CardContent classes={cardBodyAndActionStyles}>
        {isLiveContentAvailable &&
          deviceCardValues &&
          getLiveDatapointsContent(currentLanguage, deviceCardValues)}
        {loading && <Typography variant="body2">Loading...</Typography>}
        {!isLiveContentAvailable && !error && !loading && (
          <Typography variant="body2">No live content available.</Typography>
        )}
        {!isLiveContentAvailable && error && (
          <Typography variant="body2">No live content available. An error occured!</Typography>
        )}
      </CardContent>

      <CardActions disableSpacing classes={cardBodyAndActionStyles}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              className={classes.actionButton}
              size="small"
              component={Link}
              to={`/fleet/device/${deviceToLinkElement(device)}`}
            >
              <Trans>Show details</Trans>
            </Button>
          </Grid>
          <Grid item xs={6}>
            {isLiveContentAvailable &&
              deviceCardValues?.ValuesTimestamp !== undefined &&
              getFormattedDateTimeLabel(
                classes.lastUpdatedTime,
                deviceCardValues?.ValuesTimestamp,
                i18n
              )}
          </Grid>
        </Grid>
      </CardActions>
    </Card>
  );
};

function getLiveDatapointsContent(locale: string, deviceCardValues: TEMDeviceCardValues) {
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <LabeledIconRenderer
            icon={PowerIcon}
            label={getAnalogValueLabel(locale, deviceCardValues.PowerInPercent, "%")}
          />
        </Grid>
        <Grid item xs={6}>
          <LabeledIconRenderer
            icon={OperationStateIcon}
            label={getDiscreteValueLabel(deviceCardValues.OperationState)}
          />
        </Grid>
      </Grid>
      <Hidden xsDown>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <LabeledIconRenderer
              icon={OperationhoursIcon}
              label={getAnalogValueLabel(locale, deviceCardValues.HoursOfLoadRun, "h")}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <LabeledIconRenderer
              icon={EngineStartsIcon}
              label={getAnalogValueLabel(locale, deviceCardValues.NumberOfStarts)}
            />
          </Grid>
          <Grid item xs={6}>
            <LabeledIconRenderer
              icon={NextMaintenanceIcon}
              label={getAnalogValueLabel(locale, deviceCardValues.HoursUntilNextMaintenance, "h")}
            />
          </Grid>
        </Grid>
      </Hidden>
    </>
  );
}

export default TEMDeviceCard;
